import { useState } from "react";
import { ChevronRight, MapPin, Mail, Phone, X, Sun, Zap, BarChart3, Settings, Wrench } from "lucide-react";
import { useContent } from "../context/ContentContext";
import useFadeUp from "../hooks/useFadeUp";
import solarFarm   from "../assets/solar_farm.jpg";
import galEvent    from "../assets/gal_event.jpg";
import galBattery  from "../assets/gal_battery.jpg";
import photoBo     from "../assets/photo_bo.jpg";
import photoEo     from "../assets/photo_eo.jpg";
import photoJw     from "../assets/photo_jw.jpg";
import photoOm     from "../assets/photo_om.jpg";
import "./Home.css";

function Hero({ hero }) {
  const r0=useFadeUp(0),r1=useFadeUp(150),r2=useFadeUp(300),r3=useFadeUp(450);
  return (
    <section className="hero" id="home">
      <div className="hero-bg"><img src={solarFarm} alt="Solar Farm"/><div className="hero-overlay"/></div>
      <div className="hero-content container">
        <p ref={r0} className="fade-up hero-eyebrow"><span className="eline"/>Clean Energy for Nigeria's Communities<span className="eline"/></p>
        <h1 ref={r1} className="fade-up hero-h1">Powering Communities.<br/><span className="grad">Building Futures.</span></h1>
        <p ref={r2} className="fade-up hero-sub">{hero.subtext}</p>
        <div ref={r3} className="fade-up hero-btns">
          <button className="btn btn-primary" onClick={()=>document.getElementById("services")?.scrollIntoView({behavior:"smooth"})}>{hero.cta1} <ChevronRight size={16}/></button>
          <button className="btn btn-outline"  onClick={()=>document.getElementById("contact")?.scrollIntoView({behavior:"smooth"})}>{hero.cta2}</button>
        </div>
      </div>
      <div className="hero-scroll-cue" aria-hidden><div className="scroll-dot"/><span>Scroll</span></div>
    </section>
  );
}

const ABOUT_CARDS = [
  { key:"p1", title:"Who We Are",     style:{ color:"var(--white)" } },
  { key:"p2", title:"Our People",     style:{ color:"var(--white)" } },
  { key:"p3", title:"Certifications", style:{ color:"var(--white)" } },
  { key:"p4", title:"Our Heritage",   style:{ color:"var(--white)" } },
  { key:"p5", title:"Our Portfolio",  style:{ color:"var(--white)" } },
  { key:"p6", title:"Green Finance",  style:{ color:"var(--green)", textShadow:"0 0 14px rgba(31,196,122,.55)" } },
  { key:"vision",  title:"Vision",    style:{ color:"var(--lemon)", textShadow:"0 0 14px rgba(245,230,66,.55)" } },
  { key:"mission", title:"Mission",   style:{ color:"var(--accent)" } },
];

function AboutCard({ c, i, about }) {
  const r = useFadeUp(i*60);
  return (
    <div ref={r} className="fade-up card about-card">
      <h4 style={c.style}>{c.title}</h4>
      <p>{about[c.key]}</p>
    </div>
  );
}

function About({ about }) {
  return (
    <section className="section" id="about">
      <div className="container">
        <div className="section-header">
          <div className="tag">About Everlink</div>
          <h2 className="section-title">Three Decades of<br/><span className="grad">Powering Nigeria</span></h2>
          <p className="section-sub">Incorporated in 1991 and headquartered in Warri, Delta State with an office in Abuja, Everlink combines decades of technical delivery experience with a growing portfolio of renewable energy projects across Nigeria.</p>
        </div>
        <div className="about-grid-new">
          {ABOUT_CARDS.map((c,i)=><AboutCard key={c.key} c={c} i={i} about={about}/>)}
        </div>
      </div>
    </section>
  );
}

const SERVICES = [
  { icon:Sun,      title:"Mini-Grid Solutions",                     desc:"Scalable solar-powered mini-grid systems designed to deliver 24-hour electricity to off-grid and underserved communities across Nigeria." },
  { icon:Zap,      title:"Captive Power Solutions",                 desc:"Dedicated hybrid power systems for businesses, institutions, and industrial facilities requiring uninterrupted, cost-effective electricity." },
  { icon:BarChart3,title:"Professional Energy Audit",               desc:"Comprehensive energy efficiency assessments that identify consumption patterns, losses, and savings opportunities for organisations." },
  { icon:Settings, title:"Engineering, Procurement & Construction", desc:"End-to-end EPC services — from feasibility and design through procurement, installation, and commissioning of renewable energy projects." },
  { icon:Wrench,   title:"Operations and Maintenance",              desc:"24/7 remote monitoring, preventive maintenance, and rapid-response field support to ensure maximum system uptime and performance." },
];

function SvcCard({ svc, i, wide }) {
  const r = useFadeUp(i * 80);
  const Icon = svc.icon;
  return (
    <div ref={r} className={`fade-up card svc-grid-card${wide?" svc-top-card":""}`}>
      <div className="svc-icon-wrap"><Icon size={22} strokeWidth={1.6}/></div>
      <h4 className="svc-grid-title">{svc.title}</h4>
      <p className="svc-grid-desc">{svc.desc}</p>
    </div>
  );
}

function Services() {
  return (
    <section className="section services-section" id="services">
      <div className="container">
        <div className="section-header"><div className="tag">What We Offer</div><h2 className="section-title">Our <span className="grad">Services</span></h2></div>
        <div className="svc-pyramid">
          <div className="svc-row"><SvcCard svc={SERVICES[0]} i={0} wide/></div>
          <div className="svc-row"><SvcCard svc={SERVICES[1]} i={1}/><SvcCard svc={SERVICES[2]} i={2}/></div>
          <div className="svc-row"><SvcCard svc={SERVICES[3]} i={3}/><SvcCard svc={SERVICES[4]} i={4}/></div>
        </div>
      </div>
    </section>
  );
}

const PROJECTS = [
  { id:"p1", title:"Akugbene Solar PV System",  capacity:"644 kWp",  location:"Bomadi LGA, Delta State",  tag:"REA-NEP",     desc:"644 kWp solar PV system commissioned under the REA Nigeria Electrification Project (NEP), serving the Akugbene community in Bomadi LGA, Delta State.", photo:null },
  { id:"p2", title:"Egodor Solar PV System",    capacity:"50 kWp",  location:"Egodor, Delta State",       tag:"REF-REA",     desc:"50 kWp solar PV system delivered under the Rural Electrification Fund programme, providing clean reliable electricity to the Egodor community.", photo:null },
  { id:"p3", title:"Mogadishu Barracks Hybrid", capacity:"466 kWp",  location:"Nigerian Army, Abuja, FCT", tag:"Commissioned", desc:"466 kW hybrid system combining 201 kW solar and 265 kW gas for the Nigerian Army at Mogadishu Barracks, Abuja, FCT.", photo:null },
  { id:"p4", title:"Tseghem Solar PV System",   capacity:"50 kWp",  location:"Tseghem, Benue State",      tag:"REF-REA",     desc:"50 kWp solar PV system under the REF-REA programme serving Tseghem community in Benue State.", photo:null },
];

function ProjCard({ p, onClick, i }) {
  const r = useFadeUp(i * 80);
  return (
    <div ref={r} className="fade-up proj-card-new" onClick={onClick}>
      <div className="proj-top"><div className="proj-capacity">{p.capacity.split(' ')[0]} <span className="proj-unit">{p.capacity.split(' ')[1]}</span></div></div>
      <div className="proj-divider"/>
      <div className="proj-bottom">
        <div className="proj-name-new">{p.title}</div>
        <div className="proj-loc-new"><MapPin size={11}/> {p.location} · {p.tag}</div>
        <div className="proj-desc-new">{p.desc}</div>
      </div>
    </div>
  );
}

function Projects() {
  const [modal, setModal] = useState(null);
  return (
    <section className="section" id="projects">
      <div className="container">
        <div className="section-header"><div className="tag">Track Record</div><h2 className="section-title">Featured <span className="grad">Projects</span></h2><p className="section-sub">Delivering operational mini-grids and renewable energy infrastructure across Nigeria.</p></div>
        <div className="proj-grid-new">{PROJECTS.map((p,i)=><ProjCard key={p.id} p={p} i={i} onClick={()=>setModal(p)}/>)}</div>
      </div>
      {modal&&(
        <div className="modal-overlay" onClick={()=>setModal(null)}>
          <div className="modal-box card" onClick={e=>e.stopPropagation()}>
            <button className="modal-close" onClick={()=>setModal(null)}>✕</button>
            <div className="modal-img"><img src={modal.photo} alt={modal.title} style={{width:"100%",height:"100%",objectFit:"cover"}}/></div>
            <p className="proj-badge" style={{display:"inline-block",marginBottom:".8rem"}}>{modal.capacity}</p>
            <p style={{fontSize:".72rem",color:"var(--gray)",marginBottom:".4rem"}}><MapPin size={13}/> {modal.location} · {modal.tag}</p>
            <h3 className="modal-title">{modal.title}</h3>
            <p style={{color:"var(--gray)",fontSize:".9rem",lineHeight:1.7}}>{modal.desc}</p>
          </div>
        </div>
      )}
    </section>
  );
}

function GalItem({ src, alt, wide, i, onClick }) {
  const r = useFadeUp(i*80);
  return <div ref={r} className={`fade-up gal-item${wide?" wide":""}`} onClick={()=>onClick(src)}><img src={src} alt={alt}/></div>;
}

function Gallery() {
  const [lb, setLb] = useState(null);
  const photos = [{src:galEvent,alt:"Conference"},{src:solarFarm,alt:"Solar Farm"},{src:galBattery,alt:"Battery"}];
  return (
    <section className="section gallery-section" id="gallery">
      <div className="container">
        <div className="section-header"><div className="tag">Our Work</div><h2 className="section-title">Gallery</h2></div>
        <div className="gallery-grid">{photos.map((p,i)=><GalItem key={i} {...p} i={i} onClick={setLb}/>)}</div>
      </div>
      {lb&&<div className="lightbox" onClick={()=>setLb(null)}><button className="lb-close" onClick={()=>setLb(null)}><X size={24}/></button><img src={lb} alt="" onClick={e=>e.stopPropagation()}/></div>}
    </section>
  );
}

const TEAM = [
  { name:"Benjamin Ogbalor",                  role:"Founder, Managing Director & Chief Executive Officer",     photo:photoBo, fit:"cover",   bio:"An Accredited Partnership Broker (ODI, UK) and co-drafter of Nigeria's Energy Efficiency Policy under the UNDP/GEF, he brings decades of experience across the energy, infrastructure and technology sectors." },
  { name:"Egondu Ogbalor",                    role:"Chief Operating Officer & Executive Director, Strategy",     photo:photoEo, fit:"contain", bio:"Expert in corporate governance, conflict management, and community development. Certified by the Austrian Study Centre for Peace and Conflict Studies, UNDSS, and the Peace Operation Training Institute." },
  { name:"Dr. Jimba Bamikole William",        role:"Executive Director, Energy & Infrastructure",               photo:photoJw, fit:"cover",   bio:"Ph.D. Nuclear Engineering, Texas A&M University. Retired Director of Engineering Studies at PTI. 20 years Senior Research Fellow at ABU. UN Inspector Iraq (2002–2003). Fellow of the LEAD programme." },
  { name:"Engr. Onataze Eroure Tata Messiri", role:"Executive Director, Engineering Services",                         photo:photoOm, fit:"contain", bio:"COREN-registered engineer with 40+ years in electrical engineering and gas consultancy. Former Special Technical Assistant to Delta State Commissioner for Power and Energy. ASNT Level II certified." },
];

function TeamCard({ m, i }) {
  const r = useFadeUp(i*80);
  return (
    <div ref={r} className="fade-up card team-card">
      <div className="team-photo" style={m.fit==="contain"?{background:"#fff"}:{}}><img src={m.photo} alt={m.name} style={{objectFit:m.fit,objectPosition:m.fit==="cover"?"center top":"center"}}/></div>
      <div className="team-info"><h3 className="team-name">{m.name}</h3><p className="team-role">{m.role}</p><p className="team-bio">{m.bio}</p></div>
    </div>
  );
}

function Team() {
  return (
    <section className="section team-section" id="team">
      <div className="container">
        <div className="section-header"><div className="tag">Leadership</div><h2 className="section-title">Our <span className="grad">Team</span></h2><p className="section-sub">A seasoned team combining decades of expertise in energy, engineering, nuclear science, environmental policy, and community development.</p></div>
        <div className="team-grid">{TEAM.map((m,i)=><TeamCard key={m.name} m={m} i={i}/>)}</div>
      </div>
    </section>
  );
}

function PartCard({ p, i }) {
  const r = useFadeUp(i*60);
  return <div ref={r} className="fade-up card partner-card"><h4 className="partner-name">{p.name}</h4><p className="partner-area">{p.area}</p><div className="partner-badge">✓ Active Partner</div></div>;
}

function Partners({ partners }) {
  return (
    <section className="section partners-section" id="partners">
      <div className="container">
        <div className="section-header"><div className="tag">Global Network</div><h2 className="section-title">Our <span className="grad">Partners</span></h2><p className="section-sub">Everlink works with world-class international partners to deliver cutting-edge solar energy technology across Nigeria.</p></div>
        <div className="partners-grid">{partners.map((p,i)=><PartCard key={i} p={p} i={i}/>)}</div>
      </div>
    </section>
  );
}

function Contact({ contact }) {
  return (
    <section className="section" id="contact">
      <div className="container">
        <div className="section-header"><div className="tag">Reach Us</div><h2 className="section-title">Get In <span className="grad">Touch</span></h2></div>
        <div style={{maxWidth:"520px",margin:"0 auto",display:"flex",flexDirection:"column",gap:"1.2rem"}}>
          <div className="contact-item"><div className="ci-icon"><Mail size={16}/></div><div><p className="ci-label">Email</p><a href={`mailto:${contact.email}`} className="ci-val">{contact.email}</a></div></div>
          <div className="contact-item"><div className="ci-icon"><Phone size={16}/></div><div><p className="ci-label">Telephone</p><p className="ci-val">{contact.phone}</p></div></div>
          <div className="contact-item"><div className="ci-icon">📍</div><div><p className="ci-label">Head Office</p><p className="ci-val">{contact.headOffice}</p></div></div>
          <div className="contact-item"><div className="ci-icon">📍</div><div><p className="ci-label">Abuja Office</p><p className="ci-val">{contact.abujaOffice}</p></div></div>
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  const { content, loaded } = useContent();
  if (!loaded) return <div className="page-loading"><div className="spinner"/></div>;
  const { hero, about, partners, contact } = content;
  return (
    <>
      <Hero hero={hero}/>
      <About about={about}/>
      <Services/>
      <Projects/>
      <Gallery/>
      <Team/>
      <Partners partners={partners}/>
      <Contact contact={contact}/>
    </>
  );
}
